/**
 * ФОТ — расчёт зарплаты для менеджеров отдела продаж по модели:
 *
 *   Начисление = Оклад + Мотивация
 *   Мотивация = ПРИХОДЫ × tier, где tier определяется по % выполнения
 *   плана (по СУММЕ ДОГОВОРОВ). Тиры читаются из БД (motivation_rules).
 *
 * Per-employee настройки (план в деньгах + оклад) сохраняются в БД
 * через /api/finance/payroll/configs.
 *
 * «Проверка результатов» рендерится для 4 сценариев: 0%, 75%, 100%, 120%
 * выполнения плана (как в Excel).
 */
import { useState, useMemo, useEffect, createContext, useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchPayrollConfigs, upsertPayrollConfig,
  createPayrollTeam, updatePayrollTeam, deletePayrollTeam,
  fetchTeamBreakdown, fetchEmployeeBreakdown,
  createContract, deleteContract, setContractPersonal, type ContractCreatePayload,
  closeMonth, fetchAccruals, createAccrual, addAdjustment,
  fetchMotivationRules,
  type PayrollConfigRow, type TeamBreakdownMonth, type TeamBreakdownContract,
  type TeamBreakdownPersonal, type PayrollSnapshotTier, type PayrollSnapshotAdvanceTier,
} from "@/api/finance";

/**
 * Контекст актуальных правил мотивации (из БД motivation_rules).
 * Прокидывается с верха FinancePayrollPage вниз во все JS-расчёты,
 * чтобы калькулятор использовал ТЕ ЖЕ цифры что и backend compute_payroll.
 */
const PayrollRulesContext = createContext<{
  tiers: PayrollSnapshotTier[];
  advanceTiers: PayrollSnapshotAdvanceTier[];
}>({ tiers: [], advanceTiers: [] });
function useTiers(): PayrollSnapshotTier[] {
  return useContext(PayrollRulesContext).tiers;
}
import { useRef as _useRef } from "react";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import { Avatar } from "@/components/prototype/Avatar";
import { useTaxonomy } from "@/lib/useTaxonomy";

/**
 * Группировка ФОТ по отделам, не по ролям.
 *
 * Каждая «вкладка» — отдел с заранее заданным набором ролей, попадающих
 * в калькулятор. Дивиденды — спец-вкладка только для собственников.
 */
type DeptTabKey = "sales" | "production" | "field_engineer" | "admin";

interface DeptTab {
  key: DeptTabKey;
  label: string;
  description: string;
  /** undefined = special (dividends). */
  allowedRoles?: string[];
}

const DEPT_TABS: DeptTab[] = [
  { key: "sales",      label: "Отдел продаж",          description: "Расчёт ЗП сотрудников отдела продаж",          allowedRoles: ["head_op", "op_manager"] },
  { key: "production", label: "Отдел производства",    description: "Расчёт ЗП Бригадира, строителей, Инженеров",   allowedRoles: ["foreman", "builder", "engineer"] },
  { key: "field_engineer", label: "Отдел выездов",     description: "Расчёт ЗП Выездного инженера",                 allowedRoles: ["field_engineer"] },
  { key: "admin",      label: "Административный отдел", description: "Расчёт ЗП ассистентов",                        allowedRoles: ["assistant"] },
];

// Дефолтные тиры — используются только если правил в БД нет.
const DEFAULT_TIERS: PayrollSnapshotTier[] = [
  { min_pct: 0,   max_pct: 50,  pct: 0.0,    label: "<50% плана" },
  { min_pct: 50,  max_pct: 100, pct: 0.0075, label: "50-100% плана" },
  { min_pct: 100, max_pct: 120, pct: 0.015,  label: "100-120% плана" },
  { min_pct: 120, max_pct: 999, pct: 0.022,  label: ">120% плана" },
];
const DEFAULT_ADVANCE_TIERS: PayrollSnapshotAdvanceTier[] = [
  { min_amount: 50000,  bonus: 5000 },
  { min_amount: 100000, bonus: 10000 },
  { min_amount: 300000, bonus: 15000 },
];

const SCENARIOS = [0, 75, 100, 120];

function pctTierFor(p: number, tiers: PayrollSnapshotTier[] = DEFAULT_TIERS): number {
  for (const t of tiers) {
    if (p >= t.min_pct && p < t.max_pct) return t.pct;
  }
  return 0;
}

function formatRub(v: number): string {
  return Math.round(v).toLocaleString("ru-RU") + " ₽";
}

export function FinancePayrollPage() {
  const qc = useQueryClient();
  const [activeDept, setActiveDept] = useState<DeptTabKey>("sales");

  const q = useQuery({
    queryKey: ["finance-payroll-configs"],
    queryFn: fetchPayrollConfigs,
    staleTime: 60_000,
  });

  const upsertMut = useMutation({
    mutationFn: (args: { userId: string; patch: { plan_money?: number; base_salary?: number; margin_dirty?: number } }) =>
      upsertPayrollConfig(args.userId, args.patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["finance-payroll-configs"] }),
    onError: (e: Error) => alert("Ошибка сохранения: " + e.message),
  });

  const allItems = q.data?.items ?? [];
  const activeTab = DEPT_TABS.find(t => t.key === activeDept) ?? DEPT_TABS[0];

  // Актуальные правила мотивации для активной роли (из БД motivation_rules).
  // Применяются и в текстовом описании, и в JS-калькуляторе.
  const rulesQ = useQuery({
    queryKey: ["motivation-rules-active", activeDept],
    queryFn: () => fetchMotivationRules(activeDept),
    staleTime: 60_000,
  });
  const today = new Date().toISOString().slice(0, 10);
  const activeRule = useMemo(() => {
    const list = rulesQ.data ?? [];
    const valid = list.filter(r =>
      r.valid_from <= today && (!r.valid_to || r.valid_to >= today));
    return valid[0] ?? null;
  }, [rulesQ.data, today]);
  const activeTiers = activeRule?.tiers ?? DEFAULT_TIERS;
  const activeAdvanceTiers = activeRule?.advance_tiers ?? DEFAULT_ADVANCE_TIERS;

  // Список сотрудников для текущей вкладки
  const items = useMemo(() => {
    const allowed = activeTab.allowedRoles ?? [];
    return allItems.filter(u => allowed.includes(u.role) && !u.is_superuser);
  }, [allItems, activeTab]);

  return (
    <PayrollRulesContext.Provider value={{ tiers: activeTiers, advanceTiers: activeAdvanceTiers }}>
    <FinanceLayout>

      <div className="filter-bar" style={{ flexWrap: "wrap", gap: 8, marginBottom: "var(--gap)", alignItems: "center" }}>
        <label style={{ fontSize: 12, color: "var(--mute)" }}>Отдел:</label>
        <div className="chip-row">
          {DEPT_TABS.map(t => (
            <button key={t.key}
                    className={`chip ${activeDept === t.key ? "active" : ""}`}
                    onClick={() => setActiveDept(t.key)}
                    title={t.description}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Описание вкладки + (для калькулятора) описание модели */}
      <div className="card" style={{ padding: 14, marginBottom: "var(--gap)", background: "var(--paper-2)" }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
          {activeTab.label}
        </div>
        <div style={{ fontSize: 12, color: "var(--ink-2)", lineHeight: 1.6 }}>
          {activeTab.description}.
          {activeRule ? (
            <>
              <br/>
              <strong>% мотивации</strong> (тир от выполнения плана по сумме договоров, начисляется на приходы):&nbsp;
              {activeTiers.map((t, i) => (
                <span key={i}>
                  {t.label || `${t.min_pct}-${t.max_pct}%`} → <strong>{(t.pct * 100).toFixed(2)}%</strong>{i < activeTiers.length - 1 ? " · " : ""}
                </span>
              ))}
              <br/>
              <span style={{ fontSize: 11, color: "var(--mute)" }}>
                Из правила #{activeRule.id} (действует с {activeRule.valid_from}
                {activeRule.valid_to ? ` по ${activeRule.valid_to}` : " — текущее"}).
                {(rulesQ.data?.length ?? 0) > 1 && " Изменить или добавить новое — на странице «Правила мотивации»."}
              </span>
            </>
          ) : rulesQ.isLoading ? (
            <><br/><span style={{ color: "var(--mute)" }}>Правила загружаются…</span></>
          ) : (
            <><br/><span style={{ color: "var(--brick)", display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="warn" size={11}/>Правила мотивации для роли «{activeDept}» не заданы. Создайте на странице «Правила мотивации».</span></>
          )}
        </div>
      </div>

      {q.isLoading && <div className="muted" style={{ padding: 20 }}>Загрузка…</div>}
      {q.error && <div className="alert alert-red"><div className="alert-body">{(q.error as Error).message}</div></div>}

      {!q.isLoading && !q.error && (
        items.length === 0 ? (
          <div className="card muted" style={{ padding: 32, textAlign: "center" }}>
            Сотрудников в этом отделе нет
          </div>
        ) : (
          <PayrollList items={items} allItems={allItems} qc={qc} upsertMut={upsertMut}/>
        )
      )}
    </FinanceLayout>
    </PayrollRulesContext.Provider>
  );
}



void PayrollCard;
function PayrollCard({ user, onPatch, saving, mergeCandidates, onMerged }: {
  user: PayrollConfigRow;
  onPatch: (patch: { plan_money?: number; base_salary?: number; margin_dirty?: number }) => void;
  saving: boolean;
  mergeCandidates?: PayrollConfigRow[];
  onMerged?: () => void;
}) {
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const candidates = mergeCandidates ?? [];
  const doMerge = async (otherUser: PayrollConfigRow) => {
    const teamName = `${(user.display_name || user.email).split(" ")[0]} + ${(otherUser.display_name || otherUser.email).split(" ")[0]}`;
    setMerging(true);
    setMergeOpen(false);
    try {
      await createPayrollTeam({ name: teamName, member_user_ids: [user.user_id, otherUser.user_id] });
      onMerged?.();
    } catch (e) {
      alert("Ошибка: " + (e as Error).message);
    } finally {
      setMerging(false);
    }
  };
  const [plan, setPlan] = useState<string>(String(user.plan_money || ""));
  const [salary, setSalary] = useState<string>(String(user.base_salary || ""));
  const [margin, setMargin] = useState<string>(String(user.margin_dirty || ""));
  const taxQ = useTaxonomy();
  const roleLabel = taxQ.data?.roles.find(r => r.key === user.role)?.label || user.role;
  const deptLabels = (user.departments || []).map(k =>
    taxQ.data?.departments.find(d => d.key === k)?.label || k
  );

  const planN = Number(plan) || 0;
  const salaryN = Number(salary) || 0;

  const dirty = planN !== user.plan_money || salaryN !== user.base_salary || (Number(margin) || 0) !== user.margin_dirty;

  const save = () => {
    if (!dirty || saving) return;
    onPatch({ plan_money: planN, base_salary: salaryN, margin_dirty: Number(margin) || 0 });
  };

  return (
    <div className="card" style={{ padding: 16 }}>
      {/* Заголовок */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <Avatar name={user.display_name || user.email} userId={user.user_id}
                avatarFilename={user.avatar_filename} size={40} color="#4fae4e"/>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>
            {user.display_name || user.email}
          </div>
          <div style={{ fontSize: 12, color: "var(--mute)" }}>
            {user.position || roleLabel} {deptLabels.length > 0 && `· ${deptLabels.join(", ")}`}
          </div>
        </div>
        {user.updated_at && (
          <div style={{ fontSize: 11, color: "var(--mute)" }}>
            обновлено: {new Date(user.updated_at + "Z").toLocaleString("ru-RU")}
          </div>
        )}
        <CloseMonthButton userId={user.user_id}/>
        {candidates.length > 0 && (
          <div style={{ position: "relative" }}>
            <button className="btn btn-sm btn-ghost" onClick={() => setMergeOpen(o => !o)} disabled={merging}
                    title="Объединить с другим менеджером в команду — общий план и оклад на двоих">
              {merging ? "…" : "Объединить в команду"}
            </button>
            {mergeOpen && (
              <>
                <div onClick={() => setMergeOpen(false)}
                     style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
                <div style={{
                  position: "absolute", top: "100%", right: 0, marginTop: 4,
                  background: "var(--card)", border: "1px solid var(--line)",
                  borderRadius: 6, padding: 6, zIndex: 100, minWidth: 220,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                }}>
                  <div style={{ fontSize: 11, color: "var(--mute)", padding: "4px 8px 6px", textTransform: "uppercase", letterSpacing: 0.3 }}>
                    Объединить с:
                  </div>
                  {candidates.map(c => (
                    <button key={c.user_id} className="btn btn-sm" onClick={() => doMerge(c)}
                            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", justifyContent: "flex-start", padding: "6px 8px", background: "transparent", border: "none", borderRadius: 4 }}
                            onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"}
                            onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = "transparent"}>
                      <Avatar name={c.display_name || c.email} userId={c.user_id}
                              avatarFilename={c.avatar_filename} size={22} color="#4fae4e"/>
                      <span style={{ fontSize: 13 }}>{c.display_name || c.email}</span>
                    </button>
                  ))}
                  {candidates.length === 0 && (
                    <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--mute)" }}>
                      Нет других менеджеров с этой должностью
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Вводные данные */}
      <div style={{ background: "var(--paper-2)", padding: 12, borderRadius: 6, marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 10 }}>
          Вводные данные
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 8, alignItems: "center", fontSize: 13 }}>
          <label>План в деньгах</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input className="input input-sm" type="number" value={plan} step={1000}
                   onChange={(e) => setPlan(e.target.value)}
                   style={{ width: 180, fontVariantNumeric: "tabular-nums" }}/>
            <span style={{ color: "var(--mute)", fontSize: 12 }}>₽</span>
          </div>
          <label>Маржа грязная</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input className="input input-sm" type="number" value={margin} step={1000}
                   onChange={(e) => setMargin(e.target.value)}
                   style={{ width: 180, fontVariantNumeric: "tabular-nums" }}/>
            <span style={{ color: "var(--mute)", fontSize: 12 }}>₽</span>
          </div>
          <label>Какой оклад мы готовы платить?</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input className="input input-sm" type="number" value={salary} step={1000}
                   onChange={(e) => setSalary(e.target.value)}
                   style={{ width: 180, fontVariantNumeric: "tabular-nums" }}/>
            <span style={{ color: "var(--mute)", fontSize: 12 }}>₽</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn btn-sm btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>

      <PayrollResultsTable plan={planN} salary={salaryN}/>
    </div>
  );
}


/** Группирует юзеров по team_id и рендерит TeamCard для команд + PayrollCard для одиночек. */
function PayrollList({ items, qc }: {
  items: PayrollConfigRow[];
  allItems: PayrollConfigRow[];
  qc: ReturnType<typeof useQueryClient>;
  upsertMut: { isPending: boolean; mutate: (args: { userId: string; patch: { plan_money?: number; base_salary?: number; margin_dirty?: number } }) => void };
}) {
  // Группировка
  const teams: Record<string, PayrollConfigRow[]> = {};
  const solo: PayrollConfigRow[] = [];
  for (const u of items) {
    if (u.team_id) {
      (teams[u.team_id] = teams[u.team_id] || []).push(u);
    } else {
      solo.push(u);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {Object.entries(teams).map(([tid, members]) => (
        <TeamCard key={tid} teamId={tid} members={members}
                  onChanged={() => qc.invalidateQueries({ queryKey: ["finance-payroll-configs"] })}/>
      ))}
      {solo.map(u => (
        <EmployeePayrollCard key={u.user_id} user={u}
                             onChanged={() => qc.invalidateQueries({ queryKey: ["finance-payroll-configs"] })}
                             mergeCandidates={solo.filter(o => o.user_id !== u.user_id && o.role === u.role)}/>
      ))}
    </div>
  );
}


/** Командная карточка — те же поля и проверка результатов, но с шапкой «Игорь + Маша». */
const RU_MONTH_FULL = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];

function TeamCard({ teamId, members, onChanged }: {
  teamId: string;
  members: PayrollConfigRow[];
  onChanged: () => void;
}) {
  const _tiers = useTiers();
  const first = members[0];
  const teamName = first.team_name || members.map(m => (m.display_name || m.email).split(" ")[0]).join(" + ");
  const [name, setName] = useState<string>(teamName);
  const [plan] = useState<string>(String(first.plan_money || ""));
  const [salary] = useState<string>(String(first.base_salary || ""));
  const [saving, setSaving] = useState(false);

  // Состояние split: { user_id: percent }. По умолчанию делим поровну.
  const initSplit: Record<string, number> = {};
  if (first.team_split_ratios) {
    for (const m of members) initSplit[m.user_id] = first.team_split_ratios[m.user_id] ?? Math.round(100 / members.length);
  } else {
    for (const m of members) initSplit[m.user_id] = Math.round(100 / members.length);
  }
  const [splitRatios, setSplitRatios] = useState<Record<string, number>>(initSplit);

  // Per-month overrides: {ym: amount}. Если значения нет — fallback на план/12 и base_salary.
  const [monthlyPlans, setMonthlyPlans] = useState<Record<string, number>>(first.team_monthly_plans || {});
  const [monthlySalaries, setMonthlySalaries] = useState<Record<string, number>>(first.team_monthly_salaries || {});
  // Per-month split overrides: {ym: {user_id: percent}}. Fallback на splitRatios (team default).
  const [monthlySplitRatios, setMonthlySplitRatios] = useState<Record<string, Record<string, number>>>(first.team_monthly_split_ratios || {});
  // Per-month motivation %: override согласованной мотивации (вместо tier'а по регламенту)
  const [monthlyMotivationPcts, setMonthlyMotivationPcts] = useState<Record<string, number>>(first.team_monthly_motivation_pcts || {});

  const planN = Number(plan) || 0;
  const salaryN = Number(salary) || 0;
  const splitTotal = members.reduce((sum, m) => sum + (splitRatios[m.user_id] || 0), 0);
  const splitDirty = members.some(m => (splitRatios[m.user_id] || 0) !== (first.team_split_ratios?.[m.user_id] ?? Math.round(100 / members.length)));
  const monthlyDirty = JSON.stringify(monthlyPlans) !== JSON.stringify(first.team_monthly_plans || {})
                    || JSON.stringify(monthlySalaries) !== JSON.stringify(first.team_monthly_salaries || {})
                    || JSON.stringify(monthlySplitRatios) !== JSON.stringify(first.team_monthly_split_ratios || {})
                    || JSON.stringify(monthlyMotivationPcts) !== JSON.stringify(first.team_monthly_motivation_pcts || {});
  const dirty = name !== teamName || planN !== first.plan_money || salaryN !== first.base_salary || splitDirty || monthlyDirty;

  // Год для разбивки (по дефолту — текущий)
  const [year, setYear] = useState<number>(new Date().getFullYear());

  // Свёрнутость карточки (persisted в localStorage по team_id)
  const COLLAPSE_KEY = `kb-payroll-team-collapsed-${teamId}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };

  // Свёрнутость каждого месяца отдельно (persistable).
  // По умолчанию: все месяцы СВЁРНУТЫ кроме текущего — иначе вертикальная простыня.
  const MONTH_COLLAPSE_KEY = `kb-payroll-months-collapsed-${teamId}`;
  const [collapsedMonths, setCollapsedMonths] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(MONTH_COLLAPSE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });
  function persistCollapsedMonths(next: Record<string, boolean>) {
    setCollapsedMonths(next);
    try { localStorage.setItem(MONTH_COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }
  function isMonthCollapsed(ym: string): boolean {
    if (ym in collapsedMonths) return collapsedMonths[ym];
    // дефолт: всё кроме текущего — свёрнуто
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return ym !== cur;
  }
  function toggleMonth(ym: string) {
    persistCollapsedMonths({ ...collapsedMonths, [ym]: !isMonthCollapsed(ym) });
  }
  function expandMonth(ym: string) {
    persistCollapsedMonths({ ...collapsedMonths, [ym]: false });
    setTimeout(() => {
      const el = document.getElementById(`month-${teamId}-${ym}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  const breakdownQ = useQuery({
    queryKey: ["finance-team-breakdown", teamId, year],
    queryFn: () => fetchTeamBreakdown(teamId, year),
    staleTime: 60_000,
  });

  const save = async () => {
    if (!dirty || saving) return;
    if (Math.abs(splitTotal - 100) > 0.01) {
      alert(`Сумма долей деления должна быть 100%, сейчас ${splitTotal}%`);
      return;
    }
    setSaving(true);
    try {
      await updatePayrollTeam(teamId, {
        name, plan_money: planN, base_salary: salaryN,
        split_ratios: splitRatios,
        monthly_plans: monthlyPlans,
        monthly_salaries: monthlySalaries,
        monthly_split_ratios: monthlySplitRatios,
        monthly_motivation_pcts: monthlyMotivationPcts,
      });
      onChanged();
    } catch (e) {
      alert("Ошибка сохранения: " + (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const split = async () => {
    if (!confirm(`Распустить команду «${teamName}»?\n\nЕё конфиг (план/оклад) будет удалён, у каждого сотрудника останется индивидуальный конфиг.`)) return;
    setSaving(true);
    try {
      await deletePayrollTeam(teamId);
      onChanged();
    } catch (e) {
      alert("Ошибка: " + (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, borderLeft: "3px solid #3D7A3D" }}>
      {/* Шапка */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ display: "flex" }}>
          {members.map((m, i) => (
            <div key={m.user_id} style={{ marginLeft: i === 0 ? 0 : -10, border: "2px solid var(--card)", borderRadius: "50%" }}>
              <Avatar name={m.display_name || m.email} userId={m.user_id}
                      avatarFilename={m.avatar_filename} size={40} color="#4fae4e"/>
            </div>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <input className="input" type="text" value={name} onChange={e => setName(e.target.value)}
                 style={{ fontSize: 15, fontWeight: 600, width: "100%", maxWidth: 360 }}/>
          <div style={{ fontSize: 11, color: "#3D7A3D", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4, marginTop: 4 }}>
            Команда · {members.length} сотрудника · общий план/оклад
          </div>
        </div>
        <select className="input-sm" value={year} onChange={e => setYear(Number(e.target.value))}
                style={{ fontSize: 13 }} title="Год для расчёта">
          {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-sm btn-ghost" onClick={toggleCollapsed}
                title={collapsed ? "Развернуть месяцы" : "Свернуть месяцы"}>
          {collapsed ? "▸ Развернуть" : "▾ Свернуть"}
        </button>
        <PlanTemplateMenu year={year} planN={planN} onApply={(newPlans) => {
          setMonthlyPlans(newPlans);
        }}/>
        <button className="btn btn-sm btn-ghost" onClick={split} disabled={saving}
                title="Распустить команду — каждый сотрудник станет отдельным">
          Разделить
        </button>
      </div>

      {/* Сводка по году + дележка */}
      {(() => {
        const months = breakdownQ.data?.months ?? [];
        // Annual aggregates
        let yearContracts = 0, yearContractsSum = 0;
        let yearRevenues = 0, yearRevenuesSum = 0;
        let yearTeamTotal = 0, yearMotivation = 0, yearSalaryUsed = 0;
        const memberYear: Record<string, { contracts: number; contractsSum: number; revenues: number; revenuesSum: number; payout: number }> = {};
        for (const m of members) {
          memberYear[m.user_id] = { contracts: 0, contractsSum: 0, revenues: 0, revenuesSum: 0, payout: 0 };
        }
        for (const month of months) {
          const monthPlanOverride = monthlyPlans[month.ym];
          const monthSalaryOverride = monthlySalaries[month.ym];
          const monthPlan = monthPlanOverride !== undefined ? monthPlanOverride : (planN / 12);
          const monthSalary = monthSalaryOverride !== undefined ? monthSalaryOverride : salaryN;
          // ТИР по % выполнения от СУММЫ ДОГОВОРОВ; САМА мотивация — от ПРИХОДОВ × tier.
          const completionPct = monthPlan > 0 ? (month.contracts_sum / monthPlan * 100) : 0;
          const tier = pctTierFor(completionPct, _tiers);
          const monthMotivation = month.revenues_sum * tier;
          const monthTeamTotal = monthSalary + monthMotivation;

          yearContracts += month.contracts_count;
          yearContractsSum += month.contracts_sum;
          yearRevenues += month.revenues_count;
          yearRevenuesSum += month.revenues_sum;
          yearMotivation += monthMotivation;
          yearSalaryUsed += monthSalary;
          yearTeamTotal += monthTeamTotal;

          // Effective split for this month: per-month override OR team default
          const monthSplit = monthlySplitRatios[month.ym] || {};
          for (const pm of month.per_member) {
            const my = memberYear[pm.user_id];
            if (!my) continue;
            my.contracts += pm.contracts_count;
            my.contractsSum += pm.contracts_sum;
            my.revenues += pm.revenues_count;
            my.revenuesSum += pm.revenues_sum;
            const pctEff = monthSplit[pm.user_id] ?? splitRatios[pm.user_id] ?? 0;
            const ratio = pctEff / 100;
            my.payout += monthTeamTotal * ratio;
          }
        }
        const fotPct = yearRevenuesSum > 0 ? (yearTeamTotal / yearRevenuesSum * 100) : 0;

        return (
          <div style={{ background: "var(--paper-2)", padding: 16, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 }}>
              Сводка за {year}
            </div>

            {/* KPI cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 16 }}>
              <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Договоров</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-1)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{yearContracts}</div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>штук за год</div>
              </div>
              <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Сумма договоров</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--brick)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{formatRub(yearContractsSum)}</div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>за год</div>
              </div>
              <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Приходов</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--green)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{formatRub(yearRevenuesSum)}</div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>{yearRevenues} {yearRevenues === 1 ? "приход" : "приходов"}</div>
              </div>
              <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>ФОТ за год (команда)</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{formatRub(yearTeamTotal)}</div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>оклад {formatRub(yearSalaryUsed)} + моти {formatRub(yearMotivation)}</div>
              </div>
              <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
                <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>ФОТ % от выручки</div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink-1)", marginTop: 2 }}>{yearRevenuesSum > 0 ? `${fotPct.toFixed(1)}%` : "—"}</div>
              </div>
            </div>

            {/* Per-member breakdown for the year */}
            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
              Дележка за год по сотрудникам
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${members.length}, 1fr)`, gap: 10, marginBottom: 14 }}>
              {members.map(m => {
                const my = memberYear[m.user_id];
                return (
                  <div key={m.user_id} style={{
                    background: "var(--card)", padding: 16, borderRadius: 10,
                    border: "1px solid var(--line)",
                    display: "flex", flexDirection: "column", gap: 12,
                  }}>
                    {/* Шапка: аватар + имя + доля % справа */}
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={m.display_name || m.email} userId={m.user_id}
                              avatarFilename={m.avatar_filename} size={36} color="#4fae4e"/>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {m.display_name || m.email}
                        </div>
                      </div>
                      <div style={{
                        display: "inline-flex", alignItems: "center", gap: 8,
                        background: "var(--card)", border: "1.5px solid var(--brick)",
                        borderRadius: 8, padding: "6px 14px",
                      }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Доля</span>
                        <input type="number" value={splitRatios[m.user_id] ?? 0}
                               step={1} min={0} max={100}
                               onChange={(e) => setSplitRatios(sx => ({ ...sx, [m.user_id]: Number(e.target.value) }))}
                               style={{
                                 width: 50, fontVariantNumeric: "tabular-nums", fontSize: 18, fontWeight: 700,
                                 border: "none", background: "transparent", padding: 0, textAlign: "right",
                                 color: "var(--brick)", outline: "none",
                               }}/>
                        <span style={{ fontSize: 14, fontWeight: 700, color: "var(--brick)" }}>%</span>
                      </div>
                    </div>

                    {/* Сумма к выплате */}
                    <div>
                      <div style={{ fontSize: 26, fontWeight: 700, color: "var(--green)", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
                        {formatRub(my.payout)}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
                        к выплате за год
                      </div>
                    </div>

                    {/* Статы + доли в общей картине команды */}
                    {(() => {
                      const shareContractsSum = yearContractsSum > 0 ? (my.contractsSum / yearContractsSum * 100) : 0;
                      const shareContractsCnt = yearContracts > 0 ? (my.contracts / yearContracts * 100) : 0;
                      const shareRevenues = yearRevenuesSum > 0 ? (my.revenuesSum / yearRevenuesSum * 100) : 0;
                      return (
                        <div style={{ paddingTop: 10, borderTop: "1px solid var(--line)", display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "var(--mute)" }}>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Договоров</span>
                            <span><strong style={{ color: "var(--ink-1)" }}>{my.contracts}</strong> <span style={{ color: "var(--mute-2)" }}>на</span> <strong style={{ color: "var(--brick)" }}>{formatRub(my.contractsSum)}</strong></span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Приходов</span>
                            <span><strong style={{ color: "var(--ink-1)" }}>{my.revenues}</strong> <span style={{ color: "var(--mute-2)" }}>на</span> <strong style={{ color: "var(--green)" }}>{formatRub(my.revenuesSum)}</strong></span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, paddingTop: 6, borderTop: "1px dashed var(--line)" }}>
                            <span>Доля договоров (сумма)</span>
                            <span><strong style={{ color: "var(--ink-1)" }}>{shareContractsSum.toFixed(1)}%</strong></span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Доля договоров (кол-во)</span>
                            <span><strong style={{ color: "var(--ink-1)" }}>{shareContractsCnt.toFixed(1)}%</strong></span>
                          </div>
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>Доля выручки</span>
                            <span><strong style={{ color: "var(--ink-1)" }}>{shareRevenues.toFixed(1)}%</strong></span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>

            {/* Сохранить + статус суммы долей */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 11, color: Math.abs(splitTotal - 100) > 0.01 ? "var(--brick)" : "var(--mute)" }}>
                Сумма долей: {splitTotal}% {Math.abs(splitTotal - 100) > 0.01 && "— должно быть 100%"}
              </div>
              <button className="btn btn-sm btn-primary" onClick={save} disabled={!dirty || saving}>
                {saving ? "Сохранение…" : "Сохранить"}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Помесячная разбивка по сделкам и приходам */}
      {!collapsed && breakdownQ.isLoading && <div className="muted" style={{ padding: 14 }}>Загрузка разбивки…</div>}
      {!collapsed && breakdownQ.error && (
        <div className="alert alert-red"><div className="alert-body">{(breakdownQ.error as Error).message}</div></div>
      )}
      {!collapsed && breakdownQ.data && breakdownQ.data.months.length === 0 && (
        <div className="muted" style={{ padding: 14, fontSize: 13 }}>
          В {year} году у команды нет подписанных договоров и приходов.
          Менеджеры в команде ищутся по ФИО в AmoCRM — если matches не находятся,
          проверьте имена в AmoCRM (должны совпадать с ФИО в Дашборде).
        </div>
      )}
      {!collapsed && breakdownQ.data && breakdownQ.data.months.length > 0 && (
        <>
          {/* Quick-jump chips по месяцам */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12, padding: 10, background: "var(--paper-2)", borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3, marginRight: 6, alignSelf: "center" }}>
              Перейти к месяцу:
            </span>
            {breakdownQ.data.months.map(month => {
              const [, mm] = month.ym.split("-");
              const monthCollapsed = isMonthCollapsed(month.ym);
              const hasData = month.contracts_count > 0 || month.revenues_count > 0;
              return (
                <button key={month.ym} type="button" className={`chip ${!monthCollapsed ? "active" : ""}`}
                        onClick={() => expandMonth(month.ym)}
                        title={hasData ? `${month.contracts_count} дог., ${month.revenues_count} прих.` : "пусто"}
                        style={{ fontSize: 11, padding: "4px 8px", opacity: hasData ? 1 : 0.55 }}>
                  {RU_MONTH_SHORT[Number(mm) - 1]}
                  {hasData && <span style={{ marginLeft: 4, color: "var(--green)" }}>●</span>}
                </button>
              );
            })}
            <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
              <button className="chip" type="button" onClick={() => persistCollapsedMonths(
                Object.fromEntries(breakdownQ.data!.months.map(m => [m.ym, false])))}>
                Раскрыть все
              </button>
              <button className="chip" type="button" onClick={() => persistCollapsedMonths(
                Object.fromEntries(breakdownQ.data!.months.map(m => [m.ym, true])))}>
                Свернуть все
              </button>
            </div>
          </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {breakdownQ.data.months.map(month => {
            const monthPlanOverride = monthlyPlans[month.ym];
            const monthSalaryOverride = monthlySalaries[month.ym];
            // Фолбэк: годовой план / 12, оклад как есть
            const effectiveMonthPlan = monthPlanOverride !== undefined ? monthPlanOverride : (planN / 12);
            const effectiveMonthSalary = monthSalaryOverride !== undefined ? monthSalaryOverride : salaryN;
            return (
              <MonthPanel key={month.ym} month={month}
                          monthPlan={effectiveMonthPlan} monthSalary={effectiveMonthSalary}
                          isPlanOverride={monthPlanOverride !== undefined}
                          isSalaryOverride={monthSalaryOverride !== undefined}
                          onPlanChange={(v) => setMonthlyPlans(s => {
                            const next = { ...s };
                            if (v === null) delete next[month.ym]; else next[month.ym] = v;
                            return next;
                          })}
                          onSalaryChange={(v) => setMonthlySalaries(s => {
                            const next = { ...s };
                            if (v === null) delete next[month.ym]; else next[month.ym] = v;
                            return next;
                          })}
                          onSave={save}
                          dirty={dirty}
                          saving={saving}
                          splitRatios={splitRatios}
                          monthSplitOverride={monthlySplitRatios[month.ym]}
                          onMonthSplitChange={(userId, percent) => setMonthlySplitRatios(s => {
                            const next = { ...s };
                            const ms = { ...(next[month.ym] || {}) };
                            if (percent === null) {
                              delete ms[userId];
                              if (Object.keys(ms).length === 0) delete next[month.ym];
                              else next[month.ym] = ms;
                            } else {
                              ms[userId] = percent;
                              next[month.ym] = ms;
                            }
                            return next;
                          })}
                          onResetMonthSplit={() => setMonthlySplitRatios(s => {
                            const next = { ...s };
                            delete next[month.ym];
                            return next;
                          })}
                          motivationPctOverride={monthlyMotivationPcts[month.ym]}
                          onMotivationPctChange={(pct) => setMonthlyMotivationPcts(s => {
                            const next = { ...s };
                            if (pct === null) delete next[month.ym]; else next[month.ym] = pct;
                            return next;
                          })}
                          members={members}
                          teamId={teamId}
                          collapsed={isMonthCollapsed(month.ym)}
                          onToggleCollapsed={() => toggleMonth(month.ym)}/>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}

const RU_MONTH_SHORT = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];


/** Панель «Месяц YYYY-MM» — договоры, приходы, агрегаты, расчёт мотивации. */
function MonthPanel({ month, monthPlan, monthSalary, isPlanOverride, isSalaryOverride,
                     onPlanChange, onSalaryChange, onSave, dirty, saving, splitRatios,
                     monthSplitOverride, onMonthSplitChange, onResetMonthSplit, motivationPctOverride, onMotivationPctChange, members, teamId, collapsed, onToggleCollapsed }: {
  month: TeamBreakdownMonth;
  monthPlan: number;
  monthSalary: number;
  isPlanOverride: boolean;
  isSalaryOverride: boolean;
  onPlanChange: (v: number | null) => void;
  onSalaryChange: (v: number | null) => void;
  onSave: () => void;
  dirty: boolean;
  saving: boolean;
  splitRatios: Record<string, number>;
  monthSplitOverride?: Record<string, number>;
  onMonthSplitChange: (userId: string, percent: number | null) => void;
  onResetMonthSplit: () => void;
  motivationPctOverride?: number;
  onMotivationPctChange: (pct: number | null) => void;
  members: PayrollConfigRow[];
  teamId?: string;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}) {
  // Effective split = monthly override OR team default
  const _tiers = useTiers();
  const effectiveSplit: Record<string, number> = {};
  for (const m of members) {
    effectiveSplit[m.user_id] = monthSplitOverride?.[m.user_id] ?? splitRatios[m.user_id] ?? 0;
  }
  const hasOverride = !!monthSplitOverride && Object.keys(monthSplitOverride).length > 0;
  const splitSum = Object.values(effectiveSplit).reduce((s, v) => s + v, 0);
  const [y, m] = month.ym.split("-");
  const monthLabel = `${RU_MONTH_FULL[Number(m) - 1]} ${y}`;
  const qc = useQueryClient();
  const [addContractOpen, setAddContractOpen] = useState<{ ym: string } | null>(null);

  // Если месяц свёрнут — показываем только компактную шапку с кнопками
  if (collapsed) {
    const hasData = month.contracts_count > 0 || month.revenues_count > 0;
    return (
      <div id={teamId ? `month-${teamId}-${month.ym}` : undefined}
           style={{
             border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden",
             padding: "10px 16px",
             background: hasData ? "linear-gradient(135deg, rgba(61,122,61,0.06) 0%, rgba(232,200,80,0.04) 100%)" : "var(--paper-2)",
             display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", cursor: "pointer",
           }}
           onClick={onToggleCollapsed}>
        <button type="button" style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 14, padding: 4, color: "var(--ink-1)" }}>
          ▸
        </button>
        <div style={{ fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, minWidth: 120 }}>
          {monthLabel}
        </div>
        {hasData ? (
          <div style={{ display: "flex", gap: 14, fontSize: 12, color: "var(--mute)" }}>
            <span>Договоров: <b style={{ color: "var(--brick)" }}>{formatRub(month.contracts_sum)}</b> ({month.contracts_count})</span>
            <span>Приходов: <b style={{ color: "var(--green)" }}>{formatRub(month.revenues_sum)}</b> ({month.revenues_count})</span>
          </div>
        ) : (
          <span style={{ fontSize: 12, color: "var(--mute)" }}>пусто</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }} onClick={e => e.stopPropagation()}>
          <CloseMonthMenu ym={month.ym} teamId={teamId} members={members}/>
        </div>
      </div>
    );
  }

  // Месячный таргет = monthPlan (либо override, либо план/12 fallback приходит сюда)
  const monthlyTarget = monthPlan;
  // ТИР по % выполнения от СУММЫ ДОГОВОРОВ; САМА мотивация — от ПРИХОДОВ × tier.
  const completionPct = monthlyTarget > 0 ? (month.contracts_sum / monthlyTarget * 100) : 0;
  const regulationTier = pctTierFor(completionPct, _tiers);  // % по регламенту (из БД)
  // Согласованный % = override (если задан) ИЛИ regulation
  const tier = motivationPctOverride !== undefined ? motivationPctOverride / 100 : regulationTier;
  const isMotivationOverride = motivationPctOverride !== undefined;
  const motivationFromSales = month.revenues_sum * tier;
  const teamTotal = monthSalary + motivationFromSales;
  const fotPctOfRev = month.revenues_sum > 0 ? (teamTotal / month.revenues_sum * 100) : 0;

  // Личные приходы месяца: исключены из командного пула (бэкенд),
  // мотивация = Σ ЛИЧНЫХ ПРИХОДОВ × ставку (ручной % договора либо командный tier), 100% владельцу.
  const personalByUser: Record<string, TeamBreakdownPersonal> = {};
  for (const pb of month.personal ?? []) personalByUser[pb.user_id] = pb;
  // Ставка по приходу: ручной % личного договора (если задан) либо командный tier.
  const personalRate = (pct: number | null | undefined) => (pct != null ? pct / 100 : tier);
  // Личная мотивация = Σ ЛИЧНЫХ ПРИХОДОВ × ставка (тир определяется от договоров команды).
  const personalMotivationOf = (uid: string) =>
    (personalByUser[uid]?.revenues ?? []).reduce(
      (s, r) => s + r.amount * personalRate(r.personal_motivation_pct), 0);

  // Селектор «принадлежность договора»: Командный / Только N-менеджер.
  const renderOwnerCell = (c: TeamBreakdownContract) => (
    <td>
      <select
        className="input input-sm"
        value={c.personal_user_id ?? ""}
        onChange={async (e) => {
          const v = e.target.value || null;
          try {
            await setContractPersonal(c.object_id, v, v ? (c.personal_motivation_pct ?? null) : null);
            qc.invalidateQueries({ queryKey: ["finance-team-breakdown"] });
            qc.invalidateQueries({ queryKey: ["finance-employee-breakdown"] });
          } catch (err) { window.alert("Ошибка: " + (err as Error).message); }
        }}
        style={{ fontSize: 11, maxWidth: 170 }}
        title="Командный договор или личный (мотивация только этому менеджеру)"
      >
        <option value="">Командный</option>
        {members.map(m_ => (
          <option key={m_.user_id} value={m_.user_id}>Только {m_.display_name || m_.email}</option>
        ))}
      </select>
    </td>
  );

  const renderContractDeleteCell = (c: TeamBreakdownContract) => (
    <td>
      <button
        type="button"
        title="Удалить договор"
        onClick={async (e) => {
          e.stopPropagation();
          if (!window.confirm(`Удалить договор «${c.client_name}» на ${formatRub(c.contract_sum)}?`)) return;
          try {
            await deleteContract(c.object_id);
            qc.invalidateQueries({ queryKey: ["finance-team-breakdown"] });
            qc.invalidateQueries({ queryKey: ["finance-employee-breakdown"] });
          } catch (err) { window.alert("Не удалось удалить: " + (err as Error).message); }
        }}
        style={{ background: "transparent", border: "none", color: "var(--mute-2)", cursor: "pointer", padding: 4, borderRadius: 4 }}
      >
        <Icon name="trash" size={14}/>
      </button>
    </td>
  );

  const renderClientCell = (c: TeamBreakdownContract) => (
    <td>
      {c.amo_url ? (
        <a href={c.amo_url} target="_blank" rel="noopener noreferrer"
           style={{ color: "var(--brick)", fontWeight: 700, textDecoration: "none" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>{c.client_name}<Icon name="external" size={11}/></span>
        </a>
      ) : (
        <strong>{c.client_name}</strong>
      )}
    </td>
  );

  const memberById: Record<string, PayrollConfigRow> = {};
  for (const m_ of members) memberById[m_.user_id] = m_;

  return (
    <>
    <div id={teamId ? `month-${teamId}-${month.ym}` : undefined} style={{
      border: "1px solid var(--line)", borderRadius: 12, overflow: "hidden",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    }}>
      {/* === Шапка месяца === */}
      <div style={{
        background: "linear-gradient(135deg, rgba(61,122,61,0.10) 0%, rgba(232,200,80,0.06) 100%)",
        padding: "18px 20px", borderBottom: collapsed ? "none" : "1px solid var(--line)",
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap", marginBottom: collapsed ? 0 : 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {onToggleCollapsed && (
              <button type="button" onClick={onToggleCollapsed}
                      style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 18, padding: 4, color: "var(--ink-1)" }}
                      title={collapsed ? "Развернуть месяц" : "Свернуть месяц"}>
                {collapsed ? "▸" : "▾"}
              </button>
            )}
            <div style={{ fontSize: 18, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6 }}>
              {monthLabel}
            </div>
            {/* Кнопки закрытия месяца */}
            <CloseMonthMenu ym={month.ym} teamId={teamId} members={members}/>
          </div>
          {/* KPI mini-cards: договоры + приходы */}
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{
              background: "var(--card)", border: "1px solid rgba(176,49,49,0.25)",
              borderRadius: 10, padding: "10px 14px", minWidth: 160,
            }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
                Договоров заключено
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <strong style={{ fontSize: 20, color: "var(--brick)", fontVariantNumeric: "tabular-nums" }}>{formatRub(month.contracts_sum)}</strong>
              </div>
              <div style={{ fontSize: 11, color: "var(--mute)" }}>
                <strong style={{ color: "var(--ink-1)" }}>{month.contracts_count}</strong> {month.contracts_count === 1 ? "договор" : "договоров"}
              </div>
            </div>
            <div style={{
              background: "var(--card)", border: "1px solid rgba(61,122,61,0.30)",
              borderRadius: 10, padding: "10px 14px", minWidth: 160,
            }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 2 }}>
                Приходов получено
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <strong style={{ fontSize: 20, color: "var(--green)", fontVariantNumeric: "tabular-nums" }}>{formatRub(month.revenues_sum)}</strong>
              </div>
              <div style={{ fontSize: 11, color: "var(--mute)" }}>
                <strong style={{ color: "var(--ink-1)" }}>{month.revenues_count}</strong> {month.revenues_count === 1 ? "приход" : "приходов"}
              </div>
            </div>
          </div>
        </div>

        {/* Inputs (план + оклад) — pill style */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 100, padding: "4px 12px",
          }}>
            <label style={{ color: "var(--mute)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>План:</label>
            <input className="input input-sm" type="number" step={10000}
                   value={isPlanOverride ? String(monthPlan) : ""}
                   placeholder={Math.round(monthPlan).toLocaleString("ru-RU")}
                   onChange={(e) => {
                     const v = e.target.value.trim();
                     onPlanChange(v === "" ? null : Number(v));
                   }}
                   style={{ width: 110, fontVariantNumeric: "tabular-nums", border: "none", background: "transparent", padding: "2px 0", textAlign: "right" }}/>
            <span style={{ color: "var(--mute)", fontSize: 11 }}>₽</span>
            {!isPlanOverride && <span style={{ color: "var(--mute)", fontSize: 10, fontStyle: "italic" }}>авто</span>}
            {isPlanOverride && (
              <button type="button" onClick={() => onPlanChange(null)} title="Сброс"
                      style={{ background: "transparent", border: "none", color: "var(--mute)", cursor: "pointer", padding: 0, fontSize: 14 }}>×</button>
            )}
          </div>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 100, padding: "4px 12px",
          }}>
            <label style={{ color: "var(--mute)", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>Оклад:</label>
            <input className="input input-sm" type="number" step={1000}
                   value={isSalaryOverride ? String(monthSalary) : ""}
                   placeholder={Math.round(monthSalary).toLocaleString("ru-RU")}
                   onChange={(e) => {
                     const v = e.target.value.trim();
                     onSalaryChange(v === "" ? null : Number(v));
                   }}
                   style={{ width: 90, fontVariantNumeric: "tabular-nums", border: "none", background: "transparent", padding: "2px 0", textAlign: "right" }}/>
            <span style={{ color: "var(--mute)", fontSize: 11 }}>₽</span>
            {!isSalaryOverride && <span style={{ color: "var(--mute)", fontSize: 10, fontStyle: "italic" }}>авто</span>}
            {isSalaryOverride && (
              <button type="button" onClick={() => onSalaryChange(null)} title="Сброс"
                      style={{ background: "transparent", border: "none", color: "var(--mute)", cursor: "pointer", padding: 0, fontSize: 14 }}>×</button>
            )}
          </div>
          {dirty && (
            <button type="button" onClick={onSave} disabled={saving}
                    title="Сохранить изменения в этом месяце (план/оклад)"
                    style={{
                      background: "var(--brick)", color: "#fff", border: "none",
                      borderRadius: 100, padding: "5px 14px", fontSize: 11, fontWeight: 600,
                      cursor: saving ? "wait" : "pointer", letterSpacing: 0.3,
                    }}>
              {saving ? "Сохранение…" : "Сохранить"}
            </button>
          )}
        </div>
      </div>

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 14 }}>
        {/* Договоры */}
        {month.contracts.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 4, height: 16, background: "var(--brick)", borderRadius: 2 }}/>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Договоры заключённые
              </div>
              <button
                type="button"
                onClick={() => setAddContractOpen({ ym: month.ym })}
                style={{
                  marginLeft: "auto", background: "transparent",
                  border: "1px solid var(--brick)", color: "var(--brick)",
                  padding: "3px 10px", borderRadius: 6, cursor: "pointer",
                  fontSize: 11, fontWeight: 600,
                }}
              >+ Добавить договор</button>
            </div>
            <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Клиент</th>
                  <th>Менеджер</th>
                  <th className="num">Сумма</th>
                  <th>Принадлежность</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {month.contracts.map(c => (
                  <tr key={c.object_id}>
                    <td className="tabular">{c.signed_at}</td>
                    {renderClientCell(c)}
                    <td>{c.manager_name || "—"}</td>
                    <td className="num tabular">{formatRub(c.contract_sum)}</td>
                    {renderOwnerCell(c)}
                    {renderContractDeleteCell(c)}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "var(--paper-2)", fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                  <td colSpan={3} style={{ padding: "8px 12px", textTransform: "uppercase", letterSpacing: 0.3, fontSize: 11 }}>
                    Итого {month.contracts_count} {month.contracts_count === 1 ? "договор" : "договоров"}
                  </td>
                  <td className="num tabular" style={{ padding: "8px 12px", color: "var(--brick)", fontSize: 14 }}>
                    {formatRub(month.contracts_sum)}
                  </td>
                  <td/><td/>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Личные договоры менеджеров — отдельные секции */}
        {(month.personal ?? []).map(pb => {
          const pMotiv = personalMotivationOf(pb.user_id);
          return (
            <div key={pb.user_id}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <div style={{ width: 4, height: 16, background: "#d4a73a", borderRadius: 2 }}/>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Договоры только {pb.display_name || "—"}
                </div>
              </div>
              <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
                <thead>
                  <tr>
                    <th>Дата</th><th>Клиент</th><th>Менеджер</th>
                    <th className="num">Сумма</th><th>Принадлежность</th>
                    <th>% мотивации</th><th style={{ width: 36 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {pb.contracts.map(c => (
                    <tr key={c.object_id}>
                      <td className="tabular">{c.signed_at}</td>
                      {renderClientCell(c)}
                      <td>{c.manager_name || "—"}</td>
                      <td className="num tabular">{formatRub(c.contract_sum)}</td>
                      {renderOwnerCell(c)}
                      <PersonalMotivationCell
                        key={`pmc-${c.object_id}-${c.personal_motivation_pct ?? "auto"}`}
                        contract={c}
                        fallbackTier={tier}
                        onSaved={() => {
                          qc.invalidateQueries({ queryKey: ["finance-team-breakdown"] });
                          qc.invalidateQueries({ queryKey: ["finance-employee-breakdown"] });
                        }}
                      />
                      {renderContractDeleteCell(c)}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: "var(--paper-2)", fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                    <td colSpan={3} style={{ padding: "8px 12px", textTransform: "uppercase", letterSpacing: 0.3, fontSize: 11 }}>
                      Итого {pb.contracts_count} {pb.contracts_count === 1 ? "договор" : "договоров"}
                    </td>
                    <td className="num tabular" style={{ padding: "8px 12px", color: "var(--brick)", fontSize: 14 }}>
                      {formatRub(pb.contracts_sum)}
                    </td>
                    <td/><td/><td/>
                  </tr>
                </tfoot>
              </table>
              {/* Подробная таблица личных приходов */}
              {pb.revenues.length > 0 && (
                <table className="tbl tbl-tight" style={{ marginTop: 8, fontSize: 12, width: "100%" }}>
                  <thead>
                    <tr style={{ background: "rgba(212,167,58,0.10)" }}>
                      <th style={{ width: 90 }}>Дата</th>
                      <th>Объект</th>
                      <th>Контрагент</th>
                      <th className="num" style={{ width: 110 }}>Сумма</th>
                      <th className="num" style={{ width: 100 }}>Мотивация</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pb.revenues.map(r => {
                      const rate = r.personal_motivation_pct != null ? r.personal_motivation_pct / 100 : tier;
                      const m = r.amount * rate;
                      return (
                        <tr key={r.id}>
                          <td className="tabular">{r.received_at}</td>
                          <td>{r.object_name || "—"}</td>
                          <td>{r.counterparty || <span style={{ color: "var(--mute)" }}>—</span>}</td>
                          <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(r.amount)}</td>
                          <td className="num tabular" style={{ color: "var(--green)" }}>
                            {formatRub(m)}
                            <span style={{ color: "var(--mute)", fontSize: 10, marginLeft: 4 }}>
                              ({(rate * 100).toFixed(2)}%)
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "rgba(212,167,58,0.20)", fontWeight: 700 }}>
                      <td colSpan={3} style={{ padding: "6px 10px", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>
                        Итого {pb.revenues_count} {pb.revenues_count === 1 ? "приход" : "прих."} · идёт лично {pb.display_name || "—"}
                      </td>
                      <td className="num tabular" style={{ color: "var(--green)", fontSize: 13 }}>{formatRub(pb.revenues_sum)}</td>
                      <td className="num tabular" style={{ color: "var(--green)", fontSize: 13 }}>{formatRub(pMotiv)}</td>
                    </tr>
                  </tfoot>
                </table>
              )}
              {pb.revenues.length === 0 && (
                <div style={{ marginTop: 8, padding: "6px 12px", background: "rgba(212,167,58,0.10)",
                              borderRadius: 8, fontSize: 12, color: "var(--mute)" }}>
                  Приходов по этому договору пока нет
                </div>
              )}
            </div>
          );
        })}

        {/* Приходы из ДДС */}
        {month.revenues.length > 0 && (
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <div style={{ width: 4, height: 16, background: "var(--green)", borderRadius: 2 }}/>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Приходы из ДДС
              </div>
            </div>
            <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
              <thead>
                <tr>
                  <th>Дата</th>
                  <th>Объект / контрагент</th>
                  <th>Менеджер</th>
                  <th className="num">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {month.revenues.map(r => {
                  const mn = r._member_id ? (memberById[r._member_id]?.display_name || "—") : "—";
                  return (
                    <tr key={r.id}>
                      <td className="tabular">{r.received_at}</td>
                      <td>{r.object_name || r.counterparty || "—"}</td>
                      <td>{mn}</td>
                      <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(r.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: "var(--paper-2)", fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                  <td colSpan={3} style={{ padding: "8px 12px", textTransform: "uppercase", letterSpacing: 0.3, fontSize: 11 }}>
                    Итого {month.revenues_count} {month.revenues_count === 1 ? "приход" : "приходов"}
                  </td>
                  <td className="num tabular" style={{ padding: "8px 12px", color: "var(--green)", fontSize: 14 }}>
                    {formatRub(month.revenues_sum)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Агрегаты per-member */}
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 4, height: 16, background: "#5d4ec1", borderRadius: 2 }}/>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Доли участников
            </div>
          </div>
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th className="num">Договоров</th>
                <th className="num">Сумма договоров</th>
                <th className="num">Доля по сумме</th>
                <th className="num">Доля по кол-ву</th>
                <th className="num">Приходы</th>
                <th style={{ width: 110 }}/>
              </tr>
            </thead>
            <tbody>
              {month.per_member.map(pm => (
                <tr key={pm.user_id}>
                  <td>{pm.display_name || pm.email}</td>
                  <td className="num tabular">{pm.contracts_count}</td>
                  <td className="num tabular">{formatRub(pm.contracts_sum)}</td>
                  <td className="num tabular">{pm.share_by_sum_pct.toFixed(1)}%</td>
                  <td className="num tabular">{pm.share_by_count_pct.toFixed(1)}%</td>
                  <td className="num tabular" style={{ color: "var(--green)" }}>{formatRub(pm.revenues_sum)}</td>
                  <td>
                    <AddBonusButton userId={pm.user_id} userName={pm.display_name || pm.email} ym={month.ym}/>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Расчёт ЗП — KPI-grid + детальный расклад + дележка */}
        <div style={{ background: "var(--paper-2)", padding: 16, borderRadius: 10, borderLeft: "3px solid var(--green)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
              Расчёт ФОТ
            </div>
          </div>

          {/* Top KPI cards: план / выручка / % закрытия */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 14 }}>
            <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>План мес (договоры)</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink-1)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{formatRub(monthlyTarget)}</div>
            </div>
            <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Договоры месяца (для % тира)</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--brick)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>{formatRub(month.contracts_sum)}</div>
              <div style={{ fontSize: 10, color: "var(--mute)" }}>приходы (база мотивации): {formatRub(month.revenues_sum)}</div>
            </div>
            <div style={{
              background: completionPct >= 100 ? "rgba(247,224,138,0.4)" : "var(--card)",
              padding: "10px 14px", borderRadius: 8,
              border: completionPct >= 100 ? "1px solid #d4a73a" : "1px solid var(--line)",
            }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>Закрытие плана</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: completionPct >= 100 ? "var(--green)" : "var(--ink-1)", marginTop: 2 }}>{completionPct.toFixed(1)}%</div>
            </div>
            <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3 }}>% по регламенту</div>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ink-1)", marginTop: 2 }}>{(regulationTier * 100).toFixed(2)}%</div>
            </div>
            <div style={{
              background: "var(--card)", padding: "10px 14px", borderRadius: 8,
              border: isMotivationOverride ? "1.5px solid #d4a73a" : "1px solid var(--line)",
            }}>
              <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3, display: "flex", alignItems: "center", gap: 6 }}>
                Согласовано
                {isMotivationOverride && (
                  <button type="button" onClick={() => onMotivationPctChange(null)}
                          style={{ background: "transparent", border: "none", color: "var(--mute)", fontSize: 11, cursor: "pointer", padding: 0 }}
                          title="Сбросить к регламенту">×</button>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 2, marginTop: 2 }}>
                <input type="number" step={0.01} min={0} max={100}
                       value={isMotivationOverride ? (motivationPctOverride ?? 0) : ""}
                       placeholder={(regulationTier * 100).toFixed(2)}
                       onChange={(e) => {
                         const v = e.target.value.trim();
                         onMotivationPctChange(v === "" ? null : Number(v));
                       }}
                       style={{
                         width: 64, fontSize: 16, fontWeight: 700,
                         color: isMotivationOverride ? "#d4a73a" : "var(--ink-1)",
                         border: "none", background: "transparent", padding: 0, outline: "none",
                         fontVariantNumeric: "tabular-nums",
                       }}/>
                <span style={{ fontSize: 14, fontWeight: 600, color: isMotivationOverride ? "#d4a73a" : "var(--mute)" }}>%</span>
              </div>
            </div>
          </div>

          {/* Calc breakdown table */}
          <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%", background: "var(--card)", borderRadius: 8, overflow: "hidden" }}>
            <tbody>
              <tr>
                <td style={{ width: 280, padding: "8px 14px" }}>Оклад команды</td>
                <td className="num tabular" style={{ padding: "8px 14px" }}>{formatRub(monthSalary)}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px 14px" }}>Мотивация (приходы × %)</td>
                <td className="num tabular" style={{ padding: "8px 14px" }}>{formatRub(motivationFromSales)}</td>
              </tr>
              <tr style={{ background: "rgba(61,122,61,0.10)", fontWeight: 700, borderTop: "2px solid var(--line)" }}>
                <td style={{ padding: "12px 14px", textTransform: "uppercase", letterSpacing: 0.3, fontSize: 11 }}>
                  Всего на команду (ФОТ)
                </td>
                <td className="num tabular" style={{ padding: "12px 14px", color: "var(--green)", fontSize: 18 }}>
                  {formatRub(teamTotal)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px 14px", color: "var(--mute)", fontSize: 11 }}>ФОТ % от приходов</td>
                <td className="num tabular" style={{ padding: "8px 14px", color: "var(--mute)", fontSize: 11 }}>
                  {month.revenues_sum > 0 ? `${fotPctOfRev.toFixed(2)}%` : "—"}
                </td>
              </tr>
            </tbody>
          </table>

          {/* Дележка ФОТ по членам команды (per-month overrides) */}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
              <div style={{ width: 4, height: 16, background: "#d4a73a", borderRadius: 2 }}/>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--ink-1)", textTransform: "uppercase", letterSpacing: 0.4 }}>
                Дележка по сотрудникам
              </div>
              {hasOverride && (
                <>
                  <span style={{ fontSize: 11, color: "#d4a73a", background: "rgba(247,224,138,0.4)", padding: "1px 8px", borderRadius: 10 }}>
                    переопределено
                  </span>
                  <button type="button" onClick={onResetMonthSplit}
                          style={{ background: "transparent", border: "none", color: "var(--mute)", fontSize: 11, cursor: "pointer", textDecoration: "underline" }}>
                    сбросить
                  </button>
                </>
              )}
              {Math.abs(splitSum - 100) > 0.01 && (
                <span style={{ fontSize: 11, color: "var(--brick)", marginLeft: "auto" }}>
                  Сумма долей: {splitSum}% (должно быть 100%)
                </span>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: `repeat(${members.length}, 1fr)`, gap: 10 }}>
              {members.map(m_ => {
                const pct = effectiveSplit[m_.user_id] || 0;
                const ratio = pct / 100;
                const isOverridden = monthSplitOverride?.[m_.user_id] !== undefined;
                const pMotiv = personalMotivationOf(m_.user_id);
                const memberMotivation = motivationFromSales * ratio + pMotiv;
                const memberSalary_ = monthSalary * ratio;
                const memberTotal = memberSalary_ + memberMotivation;
                return (
                  <div key={m_.user_id} style={{
                    background: "var(--card)", padding: 14, borderRadius: 10,
                    border: isOverridden ? "1px solid #d4a73a" : "1px solid var(--line)",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <Avatar name={m_.display_name || m_.email} userId={m_.user_id}
                              avatarFilename={m_.avatar_filename} size={32} color="#4fae4e"/>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-1)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {m_.display_name || m_.email}
                        </div>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 6 }}>
                          <div style={{
                            display: "inline-flex", alignItems: "center", gap: 8,
                            background: "var(--card)",
                            border: isOverridden ? "1.5px solid #d4a73a" : "1.5px solid var(--brick)",
                            borderRadius: 8, padding: "6px 14px",
                          }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4 }}>Доля</span>
                            <input type="number" value={pct} step={1} min={0} max={100}
                                   onChange={(e) => onMonthSplitChange(m_.user_id, Number(e.target.value))}
                                   style={{
                                     width: 50, fontVariantNumeric: "tabular-nums", fontSize: 18, fontWeight: 700,
                                     border: "none", background: "transparent", padding: 0, textAlign: "right",
                                     color: isOverridden ? "#d4a73a" : "var(--brick)", outline: "none",
                                   }}/>
                            <span style={{ fontSize: 14, fontWeight: 700, color: isOverridden ? "#d4a73a" : "var(--brick)" }}>%</span>
                          </div>
                          {!isOverridden && <span style={{ fontSize: 10, color: "var(--mute)", fontStyle: "italic" }}>авто</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--green)", fontVariantNumeric: "tabular-nums", textAlign: "right" }}>
                      {formatRub(memberTotal)}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--mute)", textAlign: "right", marginTop: 2 }}>
                      = оклад {formatRub(memberSalary_)} + мотивация {formatRub(memberMotivation)}
                      {pMotiv > 0 && (
                        <span style={{ color: "#d4a73a" }}> (вкл. личную {formatRub(pMotiv)})</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>

      {addContractOpen && (
        <AddContractModal
          ym={addContractOpen.ym}
          members={members}
          onClose={() => setAddContractOpen(null)}
          onCreated={() => {
            setAddContractOpen(null);
            qc.invalidateQueries({ queryKey: ["finance-team-breakdown"] });
            qc.invalidateQueries({ queryKey: ["finance-employee-breakdown"] });
          }}
        />
      )}
    </>
  );
}


/** Ячейка «% мотивации» личного договора: контролируемый ввод — мотивация
 *  пересчитывается мгновенно при наборе, без ожидания рефетча. */
function PersonalMotivationCell({
  contract, fallbackTier, onSaved,
}: {
  contract: TeamBreakdownContract;
  fallbackTier: number;
  onSaved: () => void;
}) {
  const serverPct = contract.personal_motivation_pct ?? null;
  const [val, setVal] = useState<string>(serverPct != null ? String(serverPct) : "");
  const [saving, setSaving] = useState(false);
  const num = val.trim() === "" ? null : Number(val);
  const valid = num === null || (isFinite(num) && num >= 0);
  const rate = num != null && valid ? num / 100 : fallbackTier;
  const motiv = (contract.revenues_sum ?? 0) * rate;
  const reset = () => setVal(serverPct != null ? String(serverPct) : "");
  const save = async () => {
    const v = val.trim() === "" ? null : Number(val);
    if (v !== null && (!isFinite(v) || v < 0)) { window.alert("Некорректный %"); reset(); return; }
    if ((v ?? null) === serverPct) return;
    setSaving(true);
    try {
      await setContractPersonal(contract.object_id, contract.personal_user_id ?? null, v);
      onSaved();
    } catch (err) {
      window.alert("Ошибка: " + (err as Error).message);
      reset();
    } finally {
      setSaving(false);
    }
  };
  return (
    <td>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <input
          type="number" step={0.1} min={0}
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onBlur={save}
          disabled={saving}
          placeholder={(fallbackTier * 100).toFixed(2)}
          style={{
            width: 54, fontSize: 11, textAlign: "right",
            borderColor: valid ? undefined : "var(--brick)",
          }}
          title="% мотивации по этому договору. Пусто — авто-% команды."
        />
        <span style={{ fontSize: 11, color: "var(--mute)" }}>%</span>
        <span style={{ fontSize: 11, color: "var(--green)", fontWeight: 600 }}
              title="приходы по договору × %">
          = {formatRub(motiv)}
        </span>
      </div>
    </td>
  );
}


/** Таблица «Проверка результатов» — переиспользуется в PayrollCard и TeamCard. */
function PayrollResultsTable({ plan, salary }: { plan: number; salary: number }) {
  const _tiers = useTiers();
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>
        Проверка результатов
      </div>
      <table className="tbl tbl-tight" style={{ fontSize: 12, width: "100%" }}>
        <thead>
          <tr>
            <th style={{ width: 200 }}>Параметр</th>
            {SCENARIOS.map(p => (
              <th key={p} className="num" style={{ background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined }}>{p}%</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>План</strong></td>
            {SCENARIOS.map(p => (
              <td key={p} className="num tabular" style={{ background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined }}>
                {formatRub(plan)}
              </td>
            ))}
          </tr>
          <tr>
            <td><strong>Выручка</strong></td>
            {SCENARIOS.map(p => (
              <td key={p} className="num tabular" style={{ background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined }}>
                {formatRub(plan * p / 100)}
              </td>
            ))}
          </tr>
          <tr>
            <td><strong>Оклад</strong></td>
            {SCENARIOS.map(p => (
              <td key={p} className="num tabular" style={{
                background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined,
                color: p > 0 ? "var(--ink-1)" : "var(--brick)",
              }}>
                {formatRub(p > 0 ? salary : 0)}
              </td>
            ))}
          </tr>
          <tr>
            <td>% с продаж</td>
            {SCENARIOS.map(p => {
              const tier = pctTierFor(p, _tiers);
              return (
                <td key={p} className="num tabular" style={{ background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined }}>
                  {(tier * 100).toFixed(2)}%
                </td>
              );
            })}
          </tr>
          <tr>
            <td>Сумма % с продаж</td>
            {SCENARIOS.map(p => {
              const tier = pctTierFor(p, _tiers);
              const revenue = plan * p / 100;
              return (
                <td key={p} className="num tabular" style={{ background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined }}>
                  {formatRub(revenue * tier)}
                </td>
              );
            })}
          </tr>
          <tr style={{ borderTop: "2px solid var(--line)", background: "var(--paper-2)", fontWeight: 700 }}>
            <td style={{ padding: "10px 12px" }}>ВСЕГО (ФОТ)</td>
            {SCENARIOS.map(p => {
              const tier = pctTierFor(p, _tiers);
              const revenue = plan * p / 100;
              const okladPart = p > 0 ? salary : 0;
              const total = okladPart + revenue * tier;
              return (
                <td key={p} className="num tabular" style={{
                  padding: "10px 12px",
                  background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined,
                  color: total > 0 ? "var(--green)" : "var(--brick)",
                  fontSize: 14,
                }}>
                  {formatRub(total)}
                </td>
              );
            })}
          </tr>
          <tr style={{ background: "var(--paper-2)", fontWeight: 600 }}>
            <td style={{ padding: "8px 12px" }}>ФОТ % от выручки</td>
            {SCENARIOS.map(p => {
              const tier = pctTierFor(p, _tiers);
              const revenue = plan * p / 100;
              const okladPart = p > 0 ? salary : 0;
              const total = okladPart + revenue * tier;
              const fotPct = revenue > 0 ? (total / revenue) * 100 : 0;
              return (
                <td key={p} className="num tabular" style={{
                  padding: "8px 12px",
                  background: p >= 100 ? "rgba(247,224,138,0.5)" : undefined,
                }}>
                  {revenue > 0 ? `${fotPct.toFixed(2)}%` : "—"}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
    </div>
  );
}


/** EmployeePayrollCard — то же что TeamCard, но для одного сотрудника (не в команде).
 *  Использует /payroll/employees/{id}/breakdown и /payroll/configs/{id}.
 *  splitRatios всегда {user_id: 100} — один человек получает всё. */
function EmployeePayrollCard({ user, onChanged, mergeCandidates }: {
  user: PayrollConfigRow;
  onChanged: () => void;
  mergeCandidates?: PayrollConfigRow[];
}) {
  const _tiers = useTiers();
  const taxQ = useTaxonomy();
  const roleLabel = taxQ.data?.roles.find(r => r.key === user.role)?.label || user.role;

  const [plan] = useState<string>(String(user.plan_money || ""));
  const [salary, setSalary] = useState<string>(String(user.base_salary || ""));
  const [monthlyPlans, setMonthlyPlans] = useState<Record<string, number>>(user.monthly_plans || {});
  const [monthlySalaries, setMonthlySalaries] = useState<Record<string, number>>(user.monthly_salaries || {});
  const [monthlyMotivationPcts, setMonthlyMotivationPcts] = useState<Record<string, number>>(user.monthly_motivation_pcts || {});
  const [saving, setSaving] = useState(false);

  const planN = Number(plan) || 0;
  const salaryN = Number(salary) || 0;

  const dirty =
    planN !== user.plan_money || salaryN !== user.base_salary ||
    JSON.stringify(monthlyPlans) !== JSON.stringify(user.monthly_plans || {}) ||
    JSON.stringify(monthlySalaries) !== JSON.stringify(user.monthly_salaries || {}) ||
    JSON.stringify(monthlyMotivationPcts) !== JSON.stringify(user.monthly_motivation_pcts || {});

  const [year, setYear] = useState<number>(new Date().getFullYear());

  // Свёрнутость карточки целиком
  const COLLAPSE_KEY = `kb-payroll-employee-collapsed-${user.user_id}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === "1"; } catch { return false; }
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0"); } catch { /* ignore */ }
  };

  // Свёрнутость каждого месяца (как в TeamCard). По умолчанию свёрнуты все кроме текущего.
  const MONTH_COLLAPSE_KEY = `kb-payroll-employee-months-collapsed-${user.user_id}`;
  const [collapsedMonths, setCollapsedMonths] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(MONTH_COLLAPSE_KEY);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return {};
  });
  function persistCollapsedMonths(next: Record<string, boolean>) {
    setCollapsedMonths(next);
    try { localStorage.setItem(MONTH_COLLAPSE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  }
  function isMonthCollapsed(ym: string): boolean {
    if (ym in collapsedMonths) return collapsedMonths[ym];
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    return ym !== cur;
  }
  function toggleMonth(ym: string) {
    persistCollapsedMonths({ ...collapsedMonths, [ym]: !isMonthCollapsed(ym) });
  }
  function expandMonth(ym: string) {
    persistCollapsedMonths({ ...collapsedMonths, [ym]: false });
    setTimeout(() => {
      const el = document.getElementById(`month-emp-${user.user_id}-${ym}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  }

  const breakdownQ = useQuery({
    queryKey: ["finance-employee-breakdown", user.user_id, year],
    queryFn: () => fetchEmployeeBreakdown(user.user_id, year),
    staleTime: 60_000,
  });

  const save = async () => {
    if (!dirty || saving) return;
    setSaving(true);
    try {
      await upsertPayrollConfig(user.user_id, {
        plan_money: planN, base_salary: salaryN, margin_dirty: 0,
        monthly_plans: monthlyPlans,
        monthly_salaries: monthlySalaries,
        monthly_motivation_pcts: monthlyMotivationPcts,
      });
      onChanged();
    } catch (e) {
      alert("Ошибка сохранения: " + (e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Merge dropdown (как в old PayrollCard)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const candidates = mergeCandidates ?? [];
  const doMerge = async (otherUser: PayrollConfigRow) => {
    const teamName = `${(user.display_name || user.email).split(" ")[0]} + ${(otherUser.display_name || otherUser.email).split(" ")[0]}`;
    setMerging(true); setMergeOpen(false);
    try {
      await createPayrollTeam({ name: teamName, member_user_ids: [user.user_id, otherUser.user_id] });
      onChanged();
    } catch (e) { alert("Ошибка: " + (e as Error).message); }
    finally { setMerging(false); }
  };

  // Год-агрегаты для верхней «Сводки»
  const months = breakdownQ.data?.months ?? [];
  let yearContracts = 0, yearContractsSum = 0;
  let yearRevenues = 0, yearRevenuesSum = 0;
  let yearTeamTotal = 0, yearMotivation = 0, yearSalaryUsed = 0;
  for (const m of months) {
    const mpOv = monthlyPlans[m.ym];
    const msOv = monthlySalaries[m.ym];
    const monthPlan = mpOv !== undefined ? mpOv : (planN / 12);
    const monthSalary = msOv !== undefined ? msOv : salaryN;
    // ТИР по % выполнения от СУММЫ ДОГОВОРОВ; САМА мотивация — от ПРИХОДОВ × tier.
    const completion = monthPlan > 0 ? (m.contracts_sum / monthPlan * 100) : 0;
    const mmpOv = monthlyMotivationPcts[m.ym];
    const tier = mmpOv !== undefined ? mmpOv / 100 : pctTierFor(completion, _tiers);
    const motiv = m.revenues_sum * tier;
    yearContracts += m.contracts_count;
    yearContractsSum += m.contracts_sum;
    yearRevenues += m.revenues_count;
    yearRevenuesSum += m.revenues_sum;
    yearMotivation += motiv;
    yearSalaryUsed += monthSalary;
    yearTeamTotal += monthSalary + motiv;
  }
  const fotPct = yearRevenuesSum > 0 ? (yearTeamTotal / yearRevenuesSum * 100) : 0;

  // splitRatios для MonthPanel — всегда 100% этому юзеру
  const splitRatios = { [user.user_id]: 100 };
  const members = [user];

  return (
    <div className="card" style={{ padding: 16, borderLeft: "3px solid #4fae4e" }}>
      {/* Шапка */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <Avatar name={user.display_name || user.email} userId={user.user_id}
                avatarFilename={user.avatar_filename} size={40} color="#4fae4e"/>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontSize: 15, fontWeight: 600 }}>{user.display_name || user.email}</div>
          <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
            {user.position || roleLabel}
          </div>
        </div>
        <select className="input-sm" value={year} onChange={e => setYear(Number(e.target.value))}
                style={{ fontSize: 13 }}>
          {[year - 1, year, year + 1].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button className="btn btn-sm btn-ghost" onClick={toggleCollapsed}>
          {collapsed ? "▸ Развернуть" : "▾ Свернуть"}
        </button>
        {candidates.length > 0 && (
          <div style={{ position: "relative" }}>
            <button className="btn btn-sm btn-ghost" onClick={() => setMergeOpen(o => !o)} disabled={merging}
                    title="Объединить в команду">
              {merging ? "…" : "Объединить"}
            </button>
            {mergeOpen && (
              <>
                <div onClick={() => setMergeOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
                <div style={{
                  position: "absolute", top: "100%", right: 0, marginTop: 4,
                  background: "var(--card)", border: "1px solid var(--line)",
                  borderRadius: 6, padding: 6, zIndex: 100, minWidth: 220,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                }}>
                  {candidates.map(c => (
                    <button key={c.user_id} onClick={() => doMerge(c)}
                            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 8px",
                                     background: "transparent", border: "none", borderRadius: 4, cursor: "pointer" }}>
                      <Avatar name={c.display_name || c.email} userId={c.user_id}
                              avatarFilename={c.avatar_filename} size={22} color="#4fae4e"/>
                      <span style={{ fontSize: 13 }}>{c.display_name || c.email}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Сводка за год */}
      <div style={{ background: "var(--paper-2)", padding: 16, borderRadius: 10, marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 12 }}>
          Сводка за {year}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
          <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase" }}>Договоров</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{yearContracts}</div>
            <div style={{ fontSize: 11, color: "var(--mute)" }}>штук за год</div>
          </div>
          <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase" }}>Сумма договоров</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--brick)", fontVariantNumeric: "tabular-nums" }}>{formatRub(yearContractsSum)}</div>
          </div>
          <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase" }}>Приходов</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "var(--green)", fontVariantNumeric: "tabular-nums" }}>{formatRub(yearRevenuesSum)}</div>
            <div style={{ fontSize: 11, color: "var(--mute)" }}>{yearRevenues}</div>
          </div>
          <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase" }}>ФОТ за год</div>
            <div style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{formatRub(yearTeamTotal)}</div>
            <div style={{ fontSize: 11, color: "var(--mute)" }}>{`оклад ${formatRub(yearSalaryUsed)} + моти ${formatRub(yearMotivation)}`}</div>
          </div>
          <div style={{ background: "var(--card)", padding: "10px 14px", borderRadius: 8, border: "1px solid var(--line)" }}>
            <div style={{ fontSize: 10, color: "var(--mute)", textTransform: "uppercase" }}>ФОТ % от выручки</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{yearRevenuesSum > 0 ? `${fotPct.toFixed(1)}%` : "—"}</div>
          </div>
        </div>

        {/* Дефолтный оклад месяца (план задаётся помесячно, см. помесячные плашки ниже) */}
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: 8, alignItems: "center", fontSize: 13 }}>
          <label>Оклад (мес)</label>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input className="input input-sm" type="number" value={salary} step={1000}
                   onChange={e => setSalary(e.target.value)}
                   style={{ width: 180, fontVariantNumeric: "tabular-nums" }}/>
            <span style={{ color: "var(--mute)", fontSize: 12 }}>₽ / месяц (дефолт; перебивается помесячно ниже)</span>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn btn-sm btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
        </div>
      </div>

      {/* Помесячные плашки */}
      {breakdownQ.isLoading && <div className="muted" style={{ padding: 14 }}>Загрузка…</div>}
      {!collapsed && breakdownQ.data && breakdownQ.data.months.length > 0 && (
        <>
          {/* Quick-jump chips по месяцам */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 12, padding: 10, background: "var(--paper-2)", borderRadius: 8 }}>
            <span style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.3, marginRight: 6, alignSelf: "center" }}>
              Перейти к месяцу:
            </span>
            {breakdownQ.data.months.map(month => {
              const [, mm] = month.ym.split("-");
              const monthCollapsed = isMonthCollapsed(month.ym);
              const hasData = month.contracts_count > 0 || month.revenues_count > 0;
              return (
                <button key={month.ym} type="button" className={`chip ${!monthCollapsed ? "active" : ""}`}
                        onClick={() => expandMonth(month.ym)}
                        title={hasData ? `${month.contracts_count} дог., ${month.revenues_count} прих.` : "пусто"}
                        style={{ fontSize: 11, padding: "4px 8px", opacity: hasData ? 1 : 0.55 }}>
                  {RU_MONTH_SHORT[Number(mm) - 1]}
                  {hasData && <span style={{ marginLeft: 4, color: "var(--green)" }}>●</span>}
                </button>
              );
            })}
            <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
              <button className="chip" type="button" onClick={() => persistCollapsedMonths(
                Object.fromEntries(breakdownQ.data!.months.map(m => [m.ym, false])))}>
                Раскрыть все
              </button>
              <button className="chip" type="button" onClick={() => persistCollapsedMonths(
                Object.fromEntries(breakdownQ.data!.months.map(m => [m.ym, true])))}>
                Свернуть все
              </button>
            </div>
          </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {breakdownQ.data.months.map(month => {
            const mpOv = monthlyPlans[month.ym];
            const msOv = monthlySalaries[month.ym];
            const effMonthPlan = mpOv !== undefined ? mpOv : (planN / 12);
            const effMonthSalary = msOv !== undefined ? msOv : salaryN;
            return (
              <MonthPanel key={month.ym} month={month}
                          monthPlan={effMonthPlan} monthSalary={effMonthSalary}
                          isPlanOverride={mpOv !== undefined}
                          isSalaryOverride={msOv !== undefined}
                          onPlanChange={(v) => setMonthlyPlans(s => {
                            const next = { ...s };
                            if (v === null) delete next[month.ym]; else next[month.ym] = v;
                            return next;
                          })}
                          onSalaryChange={(v) => setMonthlySalaries(s => {
                            const next = { ...s };
                            if (v === null) delete next[month.ym]; else next[month.ym] = v;
                            return next;
                          })}
                          onSave={save}
                          dirty={dirty}
                          saving={saving}
                          splitRatios={splitRatios}
                          monthSplitOverride={undefined}
                          onMonthSplitChange={() => { /* solo: split = 100% */ }}
                          onResetMonthSplit={() => { /* no-op */ }}
                          motivationPctOverride={monthlyMotivationPcts[month.ym]}
                          onMotivationPctChange={(pct) => setMonthlyMotivationPcts(s => {
                            const next = { ...s };
                            if (pct === null) delete next[month.ym]; else next[month.ym] = pct;
                            return next;
                          })}
                          members={members}
                          teamId={`emp-${user.user_id}`}
                          collapsed={isMonthCollapsed(month.ym)}
                          onToggleCollapsed={() => toggleMonth(month.ym)}/>
            );
          })}
        </div>
        </>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────────────
// AddContractModal — ручное добавление договора
// ──────────────────────────────────────────────────────────────────────
function AddContractModal({ ym, members, onClose, onCreated }: {
  ym: string;
  members: PayrollConfigRow[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [y, m] = ym.split("-");
  // Default date — last day of selected month (or today if current month)
  const defaultDate = (() => {
    const yi = Number(y), mi = Number(m);
    const ts = new Date().toISOString().slice(0, 10);
    if (yi === Number(ts.slice(0, 4)) && mi === Number(ts.slice(5, 7))) return ts;
    const lastDay = new Date(yi, mi, 0).getDate();
    return `${ym}-${String(lastDay).padStart(2, "0")}`;
  })();
  void today;
  const [date, setDate] = useState(defaultDate);
  const [clientName, setClientName] = useState("");
  const [sum, setSum] = useState("");
  const [managerId, setManagerId] = useState<string>(members[0]?.user_id || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async () => {
    setErr(null);
    const sumN = Number(sum);
    if (!clientName.trim()) { setErr("Укажи имя клиента"); return; }
    if (!Number.isFinite(sumN) || sumN <= 0) { setErr("Сумма должна быть > 0"); return; }
    setSaving(true);
    try {
      const payload: ContractCreatePayload = {
        client_name: clientName.trim(),
        contract_sum: sumN,
        signed_at: date,
        manager_user_id: managerId || null,
      };
      await createContract(payload);
      onCreated();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        zIndex: 1000, display: "grid", placeItems: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--card)", borderRadius: 12, padding: 20,
          minWidth: 420, maxWidth: 520, display: "flex", flexDirection: "column", gap: 14,
          boxShadow: "0 10px 40px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <strong style={{ fontSize: 16 }}>Добавить договор</strong>
          <button onClick={onClose} style={{ background: "transparent", border: "none", cursor: "pointer", fontSize: 20, color: "var(--mute)" }}>×</button>
        </div>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Дата подписания</span>
          <input type="date" className="input input-sm" value={date} onChange={(e) => setDate(e.target.value)}/>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Клиент</span>
          <input className="input input-sm" value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Иванов Иван"/>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Сумма, ₽</span>
          <input className="input input-sm" type="number" step={1000} value={sum} onChange={(e) => setSum(e.target.value)} placeholder="2000000"/>
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: "var(--mute)" }}>Менеджер</span>
          <select className="input input-sm" value={managerId} onChange={(e) => setManagerId(e.target.value)}>
            {members.map(mb => (
              <option key={mb.user_id} value={mb.user_id}>{mb.display_name || mb.email}</option>
            ))}
          </select>
        </label>
        {err && <div style={{ color: "var(--brick)", fontSize: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} className="btn" disabled={saving}>Отмена</button>
          <button
            onClick={onSubmit}
            disabled={saving}
            style={{
              background: "var(--brick)", color: "#fff", border: "none",
              padding: "8px 16px", borderRadius: 6, cursor: "pointer", fontWeight: 600,
            }}
          >{saving ? "Сохранение..." : "Добавить"}</button>
        </div>
      </div>
    </div>
  );
}



/* ── Кнопка «Закрыть месяц» — создаёт draft-ведомости для команды ── */
function CloseMonthButton({ teamId, userId }: { teamId?: string; userId?: string }) {
  const [open, setOpen] = useState(false);
  const [ym, setYm] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const mut = useMutation({
    mutationFn: () => closeMonth({ ym, team_id: teamId, user_id: userId }),
    onSuccess: (res) => {
      alert(`Создано ведомостей: ${res.count}. Открыть «Ведомости ЗП» для проверки.`);
      setOpen(false);
    },
    onError: (e: Error) => alert("Ошибка: " + e.message),
  });
  useEscClose(open, () => setOpen(false));
  return (
    <>
      <button className="btn btn-sm" onClick={() => setOpen(true)}
              title="Создать draft-ведомости начисления за выбранный месяц">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="report" size={12}/>Закрыть месяц</span>
      </button>
      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-window" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{
                fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700,
                flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 12,
              }}>Закрыть месяц</div>
              <button className="modal-close" onClick={() => setOpen(false)} title="Закрыть (Esc)" style={{ flexShrink: 0 }}>
                <Icon name="x" size={18}/>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, color: "var(--mute)" }}>
                Создадим draft-ведомости начисления для всех участников {teamId ? "команды" : "сотрудников"}.
                Если ведомость уже есть — пропустим. После создания зайдите в «Ведомости ЗП», проверьте и утвердите.
              </div>
              <label>
                <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Месяц</div>
                <input className="input" type="month" value={ym} onChange={e => setYm(e.target.value)}/>
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px dotted var(--line)", marginTop: 4 }}>
                <button className="btn btn-ghost" onClick={() => setOpen(false)} disabled={mut.isPending}>Отмена</button>
                <button className="btn btn-primary" onClick={() => mut.mutate()} disabled={mut.isPending}>
                  {mut.isPending ? "Создаём…" : "Создать"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* ── Меню «Закрыть месяц» — для команды и/или индивидуально ── */
function CloseMonthMenu({ ym, teamId, members }: { ym: string; teamId?: string; members: PayrollConfigRow[] }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  async function run(target: { team_id?: string; user_id?: string }, label: string) {
    setBusy(label);
    try {
      const res = await closeMonth({
        ym,
        team_id: target.team_id ?? null,
        user_id: target.user_id ?? null,
      });
      if (res.count > 0) alert(`Создано ведомостей за ${ym}: ${res.count}\n\nОткройте «Ведомости ЗП» для проверки.`);
      else alert(`Ведомости за ${ym} уже существуют (по этому таргету).`);
      setOpen(false);
    } catch (e) { alert("Ошибка: " + (e as Error).message); }
    finally { setBusy(null); }
  }
  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="btn btn-sm" onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
              title="Создать ведомость начисления зарплаты за этот месяц">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="report" size={12}/>Закрыть месяц {busy && "…"}</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
          <div style={{ position: "absolute", top: "100%", left: 0, marginTop: 4, background: "var(--card)", border: "1px solid var(--line)", borderRadius: 6, padding: 6, zIndex: 100, minWidth: 280, boxShadow: "0 4px 16px rgba(0,0,0,0.15)" }}>
            <div style={{ fontSize: 11, color: "var(--mute)", padding: "4px 8px 6px", textTransform: "uppercase", letterSpacing: 0.3 }}>
              Создать ведомость за {ym}
            </div>
            {teamId && (
              <button type="button" className="btn btn-sm" disabled={!!busy}
                      onClick={() => run({ team_id: teamId }, "team")}
                      style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", marginBottom: 4 }}>
                👥 Для всей команды ({members.length})
              </button>
            )}
            {teamId && <div style={{ fontSize: 10, color: "var(--mute)", padding: "4px 8px" }}>или для одного сотрудника (если у него есть личные договоры):</div>}
            {members.map(m => (
              <button key={m.user_id} type="button" className="btn btn-sm" disabled={!!busy}
                      onClick={() => run({ user_id: m.user_id }, m.user_id)}
                      style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", padding: "6px 10px", marginBottom: 2, background: "transparent", border: "none", borderRadius: 4 }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLElement).style.background = "transparent"}>
                <Avatar name={m.display_name || m.email} userId={m.user_id} avatarFilename={m.avatar_filename} size={20} color="#4fae4e"/>
                <span style={{ fontSize: 12 }}>👤 Только {m.display_name || m.email}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


/* ── Кнопка «+ Премия / Штраф» рядом с сотрудником ── */
function useEscClose(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, onClose]);
}

function AddBonusButton({ userId, userName, ym }: { userId: string; userName: string; ym: string }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [comment, setComment] = useState("");
  const [kind, setKind] = useState<"bonus" | "penalty">("bonus");
  const [busy, setBusy] = useState(false);
  async function submit() {
    const amt = Number(amount);
    if (!amt || amt === 0) { alert("Введите сумму"); return; }
    setBusy(true);
    try {
      const list = await fetchAccruals({ ym, user_id: userId });
      let accrualId: number;
      if (list.length > 0) accrualId = list[0].id;
      else {
        const created = await createAccrual({ user_id: userId, ym });
        accrualId = created.id;
      }
      const signed = kind === "penalty" ? -Math.abs(amt) : Math.abs(amt);
      await addAdjustment(accrualId, { kind, amount: signed, comment: comment.trim() || null });
      alert(`${kind === "bonus" ? "Премия" : "Штраф"} ${Math.round(amt).toLocaleString("ru-RU")} ₽ добавлен(а) в ведомость #${accrualId}`);
      setOpen(false); setAmount(""); setComment("");
    } catch (e) { alert("Ошибка: " + (e as Error).message); }
    finally { setBusy(false); }
  }
  useEscClose(open, () => setOpen(false));
  return (
    <>
      <button type="button" className="btn btn-sm" onClick={() => setOpen(true)}
              title="Добавить премию/штраф к ведомости этого месяца"
              style={{ fontSize: 11, padding: "4px 8px" }}>
        ✨ + Премия
      </button>
      {open && (
        <div className="modal-backdrop" onClick={() => setOpen(false)}>
          <div className="modal-window" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{
                fontFamily: "var(--serif)", fontSize: 18, fontWeight: 700,
                flex: 1, minWidth: 0,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                paddingRight: 12,
              }}>
                Премия / Штраф — {userName}
              </div>
              <button className="modal-close" onClick={() => setOpen(false)} title="Закрыть (Esc)" style={{ flexShrink: 0 }}>
                <Icon name="x" size={18}/>
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 12, color: "var(--mute)" }}>
                Период: <strong style={{ color: "var(--ink-2)" }}>{ym}</strong>. Если ведомости нет — создастся автоматически.
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button type="button" className={`chip ${kind === "bonus" ? "active" : ""}`} onClick={() => setKind("bonus")}>✨ Премия</button>
                <button type="button" className={`chip ${kind === "penalty" ? "active" : ""}`} onClick={() => setKind("penalty")}><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="warn" size={11}/>Штраф</span></button>
              </div>
              <label>
                <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Сумма (₽)</div>
                <input className="input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="5000"/>
              </label>
              <label>
                <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 4, letterSpacing: "0.04em", textTransform: "uppercase" }}>Комментарий</div>
                <input className="input" value={comment} onChange={e => setComment(e.target.value)} placeholder="за что"/>
              </label>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 8, borderTop: "1px dotted var(--line)", marginTop: 4 }}>
                <button className="btn btn-ghost" onClick={() => setOpen(false)} disabled={busy}>Отмена</button>
                <button className="btn btn-primary" onClick={submit} disabled={busy || !amount}>
                  {busy ? "..." : kind === "bonus" ? "Начислить премию" : "Начислить штраф"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


/* ── Шаблоны сезонных планов ─────────────────────────────────── */
function PlanTemplateMenu({ year, planN, onApply }: {
  year: number; planN: number;
  onApply: (plans: Record<string, number>) => void;
}) {
  const [open, setOpen] = useState(false);
  function applyEqual() {
    const each = planN / 12;
    const out: Record<string, number> = {};
    for (let m = 1; m <= 12; m++) out[`${year}-${String(m).padStart(2, "0")}`] = Math.round(each);
    onApply(out); setOpen(false);
  }
  function applySummerPeak() {
    // Лето (Июн-Авг) ×1.5; зима (Дек-Фев) ×0.5; остальное ×1.0 от планN/12
    const base = planN / 12;
    const weight: Record<number, number> = { 1: 0.5, 2: 0.5, 3: 1, 4: 1, 5: 1, 6: 1.5, 7: 1.5, 8: 1.5, 9: 1, 10: 1, 11: 1, 12: 0.5 };
    const totalW = Object.values(weight).reduce((s, v) => s + v, 0);
    const normFactor = 12 / totalW;
    const out: Record<string, number> = {};
    for (let m = 1; m <= 12; m++) {
      out[`${year}-${String(m).padStart(2, "0")}`] = Math.round(base * weight[m] * normFactor);
    }
    onApply(out); setOpen(false);
  }
  function applyClear() {
    onApply({}); setOpen(false);
  }
  return (
    <div style={{ position: "relative" }}>
      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(o => !o)}
              title="Шаблон распределения плана по месяцам">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="summary" size={12}/>Шаблон</span>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
          <div style={{
            position: "absolute", top: "100%", right: 0, marginTop: 4,
            background: "var(--card)", border: "1px solid var(--line)",
            borderRadius: 6, padding: 6, zIndex: 100, minWidth: 230,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          }}>
            <div style={{ fontSize: 11, color: "var(--mute)", padding: "4px 8px 6px", textTransform: "uppercase", letterSpacing: 0.3 }}>
              Применить шаблон
            </div>
            <button type="button" className="btn btn-sm" onClick={applyEqual}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", marginBottom: 4 }}>
              📏 Равномерно (план/12)
            </button>
            <button type="button" className="btn btn-sm" onClick={applySummerPeak}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", marginBottom: 4 }}>
              ☀ Летний пик (Июн-Авг ×1.5, зима ×0.5)
            </button>
            <button type="button" className="btn btn-sm" onClick={applyClear}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", color: "var(--brick)" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Icon name="x" size={11}/>Очистить override (вернуть план/12 по умолчанию)</span>
            </button>
            <div style={{ fontSize: 11, color: "var(--mute)", padding: "6px 8px 4px" }}>
              После выбора нажмите «Сохранить» команду.
            </div>
          </div>
        </>
      )}
    </div>
  );
}
